var _themes;

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import styled, { keyframes } from 'styled-components';
import { isMobileSafari } from 'react-device-detect';
import TscThemeName from '../constants/TscThemeName';
import TscTextInputType from '../constants/TscTextInputType';
import tscStyles from '../constants/TscStyles';
import { getClassName } from '../utils/get-class-name';
var themes = (_themes = {}, _defineProperty(_themes, TscThemeName.dawn, {
  background: tscStyles.color.ui.dawn.light,
  borderColor: tscStyles.color.ui.dawn.dark,
  color: tscStyles.color.text.mediumDark,
  placeholderColor: tscStyles.color.text.medium,
  focusColor: tscStyles.color.action.primary,
  warningColor: tscStyles.color.message.warning
}), _defineProperty(_themes, TscThemeName.dusk, {
  background: tscStyles.color.ui.dusk.dark,
  borderColor: tscStyles.color.ui.dusk.lightMedium,
  color: tscStyles.color.text.light,
  placeholderColor: tscStyles.color.text.lightMedium,
  focusColor: tscStyles.color.action.primary,
  warningColor: tscStyles.color.message.warning
}), _themes);
var focusAnimation = keyframes(["0%{box-shadow:", " 0px 0px 0px 0px}50%{box-shadow:", " 0px 0px 0px 3px}100%{box-shadow:", " 0px 0px 0px 1px}"], tscStyles.color.action.primary, tscStyles.color.action.primary, tscStyles.color.action.primary);
var StyledWarning = styled.div.withConfig({
  displayName: "TextInput__StyledWarning"
})(["font-size:.875rem;color:", ";margin:.5rem 0 0 .55rem;"], function (props) {
  return themes[props.themeName].warningColor;
});
var Container = styled.div.withConfig({
  displayName: "TextInput__Container"
})(["width:100%;> input{height:2rem;padding:0 .5rem;}> textarea{padding:0.25rem .5rem;resize:vertical;overflow-y:auto;}> textarea,> input{box-sizing:border-box;font-size:", ";font-weight:400;font-family:inherit;display:block;width:100%;line-height:1.25rem;color:", ";background:", ";border:1px solid ", ";border-radius:", ";&:disabled{opacity:.25;}&::placeholder{color:", ";opacity:1;}&::-moz-focus-inner{border:0;}&:focus{border-color:", ";outline:none;animation:", " 500ms ease;animation-fill-mode:forwards;box-shadow:0px 0px 0px 1px ", ";}}"], isMobileSafari ? '1rem' : '.875rem', function (props) {
  return themes[props.themeName].color;
}, function (props) {
  return themes[props.themeName].background;
}, function (props) {
  return props.showWarning ? themes[props.themeName].warningColor : themes[props.themeName].borderColor;
}, tscStyles.border.radius.md, function (props) {
  return themes[props.themeName].placeholderColor;
}, function (props) {
  return props.showWarning ? themes[props.themeName].warningColor : themes[props.themeName].focusColor;
}, focusAnimation, tscStyles.color.action.primary);
var enterKey = 13;
var escapeKey = 27;

var TextInput = function TextInput(_ref) {
  var ariaLabel = _ref.ariaLabel,
      _ref$autoFocus = _ref.autoFocus,
      autoFocus = _ref$autoFocus === void 0 ? false : _ref$autoFocus,
      className = _ref.className,
      _ref$disabled = _ref.disabled,
      disabled = _ref$disabled === void 0 ? false : _ref$disabled,
      errorText = _ref.errorText,
      maxNumValue = _ref.maxNumValue,
      minNumValue = _ref.minNumValue,
      onBlur = _ref.onBlur,
      onChange = _ref.onChange,
      onClick = _ref.onClick,
      onEnter = _ref.onEnter,
      onEscape = _ref.onEscape,
      onFocus = _ref.onFocus,
      onPaste = _ref.onPaste,
      placeholder = _ref.placeholder,
      _ref$readOnly = _ref.readOnly,
      readOnly = _ref$readOnly === void 0 ? false : _ref$readOnly,
      _ref$required = _ref.required,
      required = _ref$required === void 0 ? false : _ref$required,
      _ref$rows = _ref.rows,
      rows = _ref$rows === void 0 ? 1 : _ref$rows,
      sharedId = _ref.sharedId,
      tabIndex = _ref.tabIndex,
      testId = _ref.testId,
      _ref$themeName = _ref.themeName,
      themeName = _ref$themeName === void 0 ? TscThemeName.dawn : _ref$themeName,
      _ref$type = _ref.type,
      type = _ref$type === void 0 ? TscTextInputType.text : _ref$type,
      value = _ref.value;

  var onKeyDown = function onKeyDown(e) {
    if (onEnter && e.keyCode === enterKey) {
      if (rows === 1 || !e.shiftKey) {
        // Multi-line does not throw onEnter if shift is held
        onEnter(e);
        e.preventDefault();
      }
    } else if (onEscape && e.keyCode === escapeKey) {
      onEscape(e);
    }
  };

  var generalInputProps = {
    // this smells, but hesitant to change it
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    'data-test-id': testId !== null && testId !== '' ? testId : '',
    id: sharedId,
    placeholder: placeholder,
    value: value,
    required: required,
    disabled: disabled,
    onChange: onChange,
    onClick: onClick,
    tabIndex: tabIndex,
    rows: rows,
    onKeyDown: onKeyDown,
    readOnly: readOnly,
    autoFocus: autoFocus,
    onFocus: onFocus,
    onBlur: onBlur,
    onPaste: onPaste,
    'aria-label': ariaLabel,
    type: type
  };
  var numberInputProps = Object.assign({}, generalInputProps, {
    max: maxNumValue,
    min: minNumValue
  });
  var inputProps = type === TscTextInputType.number ? numberInputProps : generalInputProps;
  return _jsxs(Container, {
    className: getClassName(className, null, 'text-input-container'),
    showWarning: !!errorText,
    themeName: themeName,
    children: [rows > 1 ? _jsx("textarea", _objectSpread({}, inputProps)) : _jsx("input", _objectSpread({}, inputProps)), errorText && _jsx(StyledWarning, {
      className: getClassName(className, 'styled-warning'),
      themeName: themeName,
      children: errorText
    })]
  });
};

var TextInputTest = {
  Container: Container,
  StyledWarning: StyledWarning
};
export { TextInput, TextInputTest };
export default TextInput;